import React, { useState, useMemo } from "react";
import { AlertCircle, CheckCircle, Clock } from "lucide-react";
import {
  AudioPair,
  BrewingTransaction,
  Collections,
  PushType,
} from "../../types/type";
import { DualAudioState } from "./hooks/useDualAudio";
import { useRecoilState } from "recoil";
import {
  loadedAudioPairState,
  loadedBrewingTransactionState,
} from "../../atom/atom";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { updateSongRequestToFailOfBrewingTransaction } from "../../api/update.api";
import { sendPushNotification } from "../../api/common.api";

type FailureType = "완전실패" | "임시실패";

interface FailureModalState {
  isOpen: boolean;
  type: FailureType | null;
  reason: string;
}

const handleCompleteFailure = async (
  transaction: BrewingTransaction
): Promise<void> => {
  try {
    const transactionRef = doc(
      db,
      Collections.BREWING_TRANSACTION,
      transaction.transactionId
    );

    // Update song request status and related data
    await updateSongRequestToFailOfBrewingTransaction({
      songRequestId: transaction.songRequestId,
      userId: transaction.targetUserInfoList[0].userId,
      brewingTransactionId: transaction.transactionId,
    });

    // Send notifications to users
    const userTokenList = transaction.targetUserInfoList
      .map((userInfo) => userInfo.userToken)
      .filter((token): token is string => Boolean(token));

    if (userTokenList.length > 0) {
      const notificationResult = await sendPushNotification({
        title: "제조 실패",
        body: "곡 제조에 실패했어요 ㅠㅠ 다른 곡으로 다시 신청해주시면 열심히 만들어볼께요!",
        tokenList: userTokenList,
        data: {
          screen: "MyBrewingList",
          type: "제조실패" as PushType,
        },
      });

      // Update notification status
      const notificationsSent = transaction.targetUserInfoList.map(
        (userInfo) => {
          const notification = {
            userId: userInfo.userId,
            status:
              userInfo.userToken &&
              notificationResult.successfulTokenList.includes(
                userInfo.userToken
              )
                ? ("completed" as const)
                : ("failed" as const),
          };

          const error = userInfo.userToken
            ? notificationResult.failedTokenList.includes(userInfo.userToken)
              ? "Failed to send notification"
              : undefined
            : "No user token available";

          if (error) {
            return { ...notification, error };
          }

          return notification;
        }
      );

      await updateDoc(transactionRef, {
        status: "failed",
        notificationsSent,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Error in handleCompleteFailure:", error);
    throw error;
  }
};

const handleTemporaryFailure = async (
  transaction: BrewingTransaction
): Promise<void> => {
  try {
    const transactionRef = doc(
      db,
      Collections.BREWING_TRANSACTION,
      transaction.transactionId
    );

    await updateDoc(transactionRef, {
      status: "failed",
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error in handleTemporaryFailure:", error);
    throw error;
  }
};

export interface BrewingTransactionCardProps {
  shouldLoadAudio: boolean;
  transaction: BrewingTransaction;
  currentAudioState: DualAudioState | null;
  onPlayAudioPair: (audioPair: AudioPair, transactionId: string) => void;
  onSkipTime: (seconds: number, transactionId: string) => void;
  onTimeUpdate: (value: number, transactionId: string) => void;
  selectedAudioPair: Record<string, AudioPair>; // Key: transactionId, value: selected AudioPair
  onSelectAudioPair: (transactionId: string, audioPair: AudioPair) => void;
  onTrimAudio: (
    start: number,
    end: number,
    mode: "cut" | "mute"
  ) => AudioPair | null;
  selectedPitch: number | null;
  onSelectPitch: (pitch: number | null) => void;
}

const BrewingTransactionCard: React.FC<BrewingTransactionCardProps> = ({
  shouldLoadAudio,
  transaction,
  currentAudioState,
  onPlayAudioPair,
  onSkipTime,
  onTimeUpdate,
  selectedAudioPair,
  onSelectAudioPair,
  onTrimAudio,
  selectedPitch,
  onSelectPitch,
}) => {
  const [loadedPair, setLoadedPair] = useRecoilState(loadedAudioPairState);
  const [loadedTransaction, setLoadedTransaction] = useRecoilState(
    loadedBrewingTransactionState
  );

  const loadAudioEditor = (
    transaction: BrewingTransaction,
    pair: AudioPair
  ) => {
    // 이미 로드된 pair를 다시 클릭하면 닫기
    if (loadedPair === pair) {
      setLoadedPair(null);
      setLoadedTransaction(null);
    } else {
      // 다른 pair를 클릭하면 기존 것을 닫고 새로운 것을 열기
      setLoadedPair(pair);
      setLoadedTransaction(transaction);
    }
  };

  const filteredAudioPairs = useMemo(() => {
    // selectedPitch가 null이면 빈 배열 반환
    if (selectedPitch === null) {
      return [];
    }

    // pitch가 없는 audioPair는 0으로 간주
    return transaction.audioPairList.filter(
      (pair) => (pair.pitch ?? 0) === selectedPitch
    );
  }, [transaction.audioPairList, selectedPitch]);

  const uniquePitches = useMemo(() => {
    const pitches = new Set<number>();

    transaction.audioPairList.forEach((pair) => {
      // pitch가 없는 경우 0으로 간주
      pitches.add(pair.pitch ?? 0);
    });

    return Array.from(pitches).sort((a, b) => a - b);
  }, [transaction.audioPairList]);

  const isEditorLoaded = (pair: AudioPair) => {
    return (
      loadedPair?.vocalUrl === pair.vocalUrl &&
      loadedTransaction?.transactionId === transaction.transactionId
    );
  };

  return (
    <div style={styles.card}>
      <TransactionHeader transaction={transaction} />

      <div style={styles.expandedContent}>
        <div>
          <h3 style={styles.sectionTitle}>Pitch Selection</h3>
          <div style={styles.pitchButtons}>
            {uniquePitches.map((pitch) => (
              <button
                key={`pitch-${transaction.transactionId}-${pitch}`}
                onClick={() => onSelectPitch(pitch)}
                style={{
                  ...styles.pitchButton,
                  backgroundColor:
                    selectedPitch === pitch ? "#2563EB" : "#E5E7EB",
                  color: selectedPitch === pitch ? "white" : "#4B5563",
                }}
              >
                {pitch}
              </button>
            ))}
          </div>

          <h3 style={styles.sectionTitle}>Audio Pairs</h3>
          <div style={styles.audioPairList}>
            {filteredAudioPairs.map((pair, index) => (
              <div
                key={`${pair.vocalUrl}-${index}`}
                style={styles.audioPairRow}
              >
                <div style={styles.pairInfo}>
                  <input
                    type="checkbox"
                    checked={
                      selectedAudioPair[transaction.transactionId]?.vocalUrl ===
                      pair.vocalUrl
                    }
                    onChange={() =>
                      onSelectAudioPair(transaction.transactionId, pair)
                    }
                    style={styles.checkbox}
                  />
                  <div style={styles.pairDetails}>
                    <div style={styles.pairUrls}>
                      <span>
                        MR:{" "}
                        {(() => {
                          try {
                            const filename = pair.mrUrl.split("/").pop() || "";
                            return decodeURIComponent(filename);
                          } catch (e) {
                            return pair.mrUrl.split("/").pop() || "";
                          }
                        })()}
                      </span>
                      <span>
                        Vocal:{" "}
                        {(() => {
                          try {
                            const filename =
                              pair.vocalUrl.split("/").pop() || "";
                            return decodeURIComponent(filename);
                          } catch (e) {
                            return pair.vocalUrl.split("/").pop() || "";
                          }
                        })()}
                      </span>
                    </div>
                    <button
                      onClick={() => loadAudioEditor(transaction, pair)}
                      style={
                        isEditorLoaded(pair)
                          ? styles.loadedButton
                          : styles.loadButton
                      }
                    >
                      {isEditorLoaded(pair) ? "Close Editor" : "Load Editor"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {transaction.targetUserInfoList &&
          transaction.targetUserInfoList.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h3 style={styles.sectionTitle}>Target Users</h3>
              <div style={styles.targetUsersList}>
                {transaction.targetUserInfoList.map((user) => (
                  <div key={user.userId} style={styles.userItem}>
                    {user.userId}
                  </div>
                ))}
              </div>
            </div>
          )}
        {transaction.error && (
          <div style={styles.errorMessage}>{transaction.error}</div>
        )}
      </div>
    </div>
  );
};

interface TransactionHeaderProps {
  transaction: BrewingTransaction;
}

const headerStyles = {
  cardHeader: {},
  headerInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  text1: {
    fontSize: "14px",
    color: "black",
  },
  text2: {
    fontSize: "12px",
    color: "grey",
  },
};

const TransactionHeader: React.FC<TransactionHeaderProps> = ({
  transaction,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle color="#10B981" size={20} />;
      case "failed":
        return <AlertCircle color="#EF4444" size={20} />;
      case "pending":
        return <Clock color="#F59E0B" size={20} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "#10B981";
      case "failed":
        return "#EF4444";
      case "pending":
        return "#F59E0B";
      default:
        return "#F59E0B";
    }
  };
  const handleFailureClick = async (type: "완전실패" | "임시실패") => {
    if (!window.confirm(`${type} 처리를 진행하시겠습니까?`)) {
      return;
    }

    setIsProcessing(true);
    try {
      if (type === "완전실패") {
        await handleCompleteFailure(transaction);
      } else {
        await handleTemporaryFailure(transaction);
      }
      alert(`${type} 처리가 완료되었습니다.`);
    } catch (error) {
      console.error("Failed to process failure:", error);
      alert("실패 처리 중 오류가 발생했습니다.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      style={{
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginBottom: 4,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {/* Status 표시 */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: 2 }}>
              {getStatusIcon(transaction.status)}
            </span>
            <span style={{ color: getStatusColor(transaction.status) }}>
              {transaction.status.charAt(0).toUpperCase() +
                transaction.status.slice(1)}
            </span>
          </div>

          {/* Pending 상태일 때만 버튼 표시 */}
          {transaction.status === "pending" && (
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleFailureClick("임시실패")}
                disabled={isProcessing}
                style={{
                  padding: "4px 12px",
                  fontSize: "12px",
                  backgroundColor: "#F59E0B",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  opacity: isProcessing ? 0.5 : 1,
                }}
              >
                {isProcessing ? "처리중..." : "임시실패"}
              </button>
              <button
                onClick={() => handleFailureClick("완전실패")}
                disabled={isProcessing}
                style={{
                  padding: "4px 12px",
                  fontSize: "12px",
                  backgroundColor: "#DC2626",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  opacity: isProcessing ? 0.5 : 1,
                }}
              >
                {isProcessing ? "처리중..." : "완전실패"}
              </button>
            </div>
          )}
        </div>

        <div style={headerStyles.text2}>
          {transaction.timestamp.toDate().toLocaleString("ko-KR", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })}
        </div>
      </div>

      <div style={headerStyles.text1}>
        id: <span>{transaction.transactionId}</span>
      </div>
      <div style={headerStyles.text1}>
        제목: <span>{transaction.songTitle}</span>
      </div>
      <div style={headerStyles.text1}>
        가수명: <span>{transaction.existingArtist?.artistName}</span>
      </div>
    </div>
  );
};

const styles = {
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  basicInfo: {
    padding: "16px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  infoLabel: {
    fontSize: "12px",
    color: "#6B7280",
    marginBottom: "4px",
  },
  infoValue: {
    fontSize: "14px",
    color: "#111827",
    fontWeight: "500",
  },
  expandedContent: {
    padding: "16px",
    borderTop: "1px solid #F3F4F6",
  },
  sectionTitle: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#6B7280",
    marginBottom: "8px",
  },
  targetUsersList: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    marginTop: "8px",
  },
  userItem: {
    fontSize: "12px",
    color: "#4B5563",
    backgroundColor: "#F9FAFB",
    padding: "8px 12px",
    borderRadius: "4px",
  },
  errorMessage: {
    backgroundColor: "#FEF2F2",
    color: "#DC2626",
    padding: "12px",
    borderRadius: "6px",
    fontSize: "12px",
    marginTop: "16px",
  },
  audioRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  checkbox: {
    cursor: "pointer",
  },
  completeButton: {
    marginTop: "16px",
    padding: "8px 16px",
    backgroundColor: "#10B981",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  disabledButton: {
    marginTop: "16px",
    padding: "8px 16px",
    backgroundColor: "gray",
    pointerEvent: "none",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "500",
  },
  audioControls: {
    display: "flex",
    gap: "8px",
    marginBottom: "8px",
  },
  loadButton: {
    padding: "4px 12px",
    backgroundColor: "#2563EB",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
  },
  loadedButton: {
    padding: "4px 12px",
    backgroundColor: "#DC2626",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
  },
  pitchButtons: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
  },
  pitchButton: {
    padding: "4px 12px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    color: "white",
    transition: "background-color 0.2s",
  },
  audioPairList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  },
  audioPairRow: {
    backgroundColor: "#f3f4f6",
    borderRadius: "8px",
    padding: "12px",
  },
  pairInfo: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  pairDetails: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  pairUrls: {
    width: "100%",
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
    fontSize: "12px",
    color: "#4b5563",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
};

export default BrewingTransactionCard;
